import * as TonalNote from '@tonaljs/note';

import { BaseError } from '../../errors/base';
import { ValidationError } from '../../errors/core';
import type { PitchClass } from '../../note-path/practice';
import { TheoryServiceError } from '../errors';

function isPitchClass(n: number): n is PitchClass {
  return Number.isInteger(n) && n >= 0 && n <= 11;
}

/**
 * Gets enharmonic equivalent of a note
 * @param note - Note name (e.g. "C#", "Db")
 * @returns Enharmonic note name
 */
export function getEnharmonic(note: string): string {
  return TonalNote.enharmonic(note);
}

/**
 * Transposes a note by an interval
 * @param note - Note name (e.g. "C", "F#")
 * @param interval - Interval name (e.g. "P5", "M3", "m7")
 * @returns Resulting note name
 */
export function transposeNote(note: string, interval: string): string {
  return TonalNote.transpose(note, interval);
}

/**
 * Check if two notes are enharmonically equivalent
 */
export function areNotesEnharmonic(noteA: string, noteB: string): boolean {
  if (noteA === noteB) return true;

  return noteA === TonalNote.enharmonic(noteB) || noteB === TonalNote.enharmonic(noteA);
}

/**
 * Convert a note name to a pitch class (0..11) with C = 0.
 * Accepts enharmonics (e.g., C# = Db) and returns the same class.
 * Throws ValidationError for invalid note names.
 */
export function noteToPitchClass(note: string): PitchClass {
  const chromaVal = TonalNote.chroma(note);
  if (chromaVal === null) {
    throw new ValidationError(`Invalid note name: '${note}'`, 'INVALID_NOTE_NAME', { note });
  }
  if (!isPitchClass(chromaVal)) {
    throw new ValidationError(`Invalid pitch class for note '${note}'`, 'INVALID_PITCH_CLASS', {
      note,
      chroma: chromaVal,
    });
  }
  return chromaVal;
}

/**
 * Determines the scale degree (position) of a note within a scale
 * @param note The note to convert
 * @param rootNote The root note of the scale
 * @param scaleType The type of scale
 * @param getScaleNotes Function to get scale notes
 * @returns The scale degree as a string (1, 2, 3, etc.)
 */
export function noteToScaleDegree(
  note: string,
  rootNote: string,
  scaleType: string,
  getScaleNotesFunc: (rootNote: string, scaleType: string) => string[]
): string {
  try {
    const scaleNotes = getScaleNotesFunc(rootNote, scaleType);

    // Find the position of the note in the scale (accounting for enharmonic equivalents)
    for (let i = 0; i < scaleNotes.length; i++) {
      const scaleNote = scaleNotes[i];
      if (scaleNote && areNotesEnharmonic(note, scaleNote)) {
        // Return one-based index as scale degree
        return String(i + 1);
      }
    }

    // If note isn't in the scale, throw ValidationError
    throw new ValidationError(
      `Note '${note}' not found in scale '${rootNote} ${scaleType}'.`,
      'NOTE_NOT_IN_SCALE',
      {
        note,
        rootNote,
        scaleType,
        availableScaleNotes: scaleNotes,
      }
    );
  } catch (error) {
    // If error is already a BaseError (like TheoryServiceError from getScaleNotes), re-throw it
    if (error instanceof ValidationError || error instanceof TheoryServiceError) {
      throw error;
    }
    // Check if it's any other BaseError
    if (error instanceof BaseError) {
      throw error;
    }

    // Otherwise, wrap it in a TheoryServiceError for consistency
    const message = error instanceof Error ? error.message : String(error);
    throw new TheoryServiceError(
      `Error converting note to scale degree: ${message}`,
      'NOTE_TO_DEGREE_CONVERSION_FAILED',
      { originalError: error }
    );
  }
}
